import { lazy, Suspense, useEffect, useState, type ReactElement } from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import Lobby from './routes/Lobby'
import { useTranslation } from 'react-i18next'
import { MatchProvider } from './match/MatchProvider'
import { MeProvider } from './providers/MeProvider'
import { OverlayProvider } from './components/OverlayRoot'
import AppShell from './components/AppShell'
import { SettingsDrawer } from './components/SettingsDrawer'
import { SettingsDrawerProvider } from './components/useSettingsDrawer'
import { ProfileDrawer } from './components/ProfileDrawer'
import { ProfileDrawerProvider } from './components/useProfileDrawer'
import { LiveMatchSheet } from './components/LiveMatchSheet'
import { LiveMatchSheetProvider } from './components/useLiveMatchSheet'
import OrientationPreview from './components/OrientationPreview'
import { MaintenanceScreen } from './components/screens/MaintenanceScreen'
import { NotInTelegramScreen } from './components/screens/NotInTelegramScreen'

// Lazy-load Pixi-heavy routes so they aren't part of the launcher chunk.
const FreePlay = lazy(() => import('./routes/FreePlay'))
const Results = lazy(() => import('./routes/Results'))
const DailyChallenge = lazy(() => import('./routes/DailyChallenge'))
const DailyResults = lazy(() => import('./routes/DailyResults'))
const DailyHistory = lazy(() => import('./routes/DailyHistory'))
const Leaderboards = lazy(() => import('./routes/Leaderboards'))
const AdminGate = lazy(() => import('./routes/AdminGate'))
const AdminOverview = lazy(() => import('./routes/AdminOverview'))
const AdminAntiCheat = lazy(() => import('./routes/AdminAntiCheat'))
const AdminMatches = lazy(() => import('./routes/AdminMatches'))
const Matchmaking = lazy(() => import('./routes/Matchmaking'))
const MatchActive = lazy(() => import('./routes/MatchActive'))
const MatchResults = lazy(() => import('./routes/MatchResults'))
const MatchHistory = lazy(() => import('./routes/MatchHistory'))
const RoomLobby = lazy(() => import('./routes/RoomLobby'))

function RouteFallback(): ReactElement {
  const { t } = useTranslation()
  return (
    <div className="route-fallback">
      <p>{t('common.loading')}</p>
    </div>
  )
}

/**
 * Returns true when the app is running in a browser outside Telegram.
 * In production: platform 'unknown' means no real Telegram WebApp context.
 * In development (import.meta.env.DEV): tolerated — returns false so local
 * preview works normally.
 */
function isNotInTelegram(): boolean {
  if (typeof window === 'undefined') return false
  if (import.meta.env.DEV) return false
  const platform = window.Telegram?.WebApp?.platform
  return !platform || platform === 'unknown'
}

function useMaintenanceMode(): boolean {
  const [maintenance, setMaintenance] = useState(false)

  useEffect(() => {
    const handler = () => setMaintenance(true)
    window.addEventListener('app:maintenance', handler)
    return () => window.removeEventListener('app:maintenance', handler)
  }, [])

  return maintenance
}

/**
 * Wrapper that mounts a single <MatchProvider> for the 3 live-match routes.
 * The provider owns the WS lifecycle so the connection survives navigation
 * between /matchmaking → /match → /match/results. Other routes (Lobby,
 * Daily, FreePlay, Leaderboards, Admin) are unaffected.
 *
 * /match/history is intentionally outside this group — it only needs a
 * REST fetch, not a live WS.
 */
function MatchRoutesWrapper(): ReactElement {
  return (
    <MatchProvider>
      <Outlet />
    </MatchProvider>
  )
}

export default function App(): ReactElement {
  const maintenance = useMaintenanceMode()

  if (isNotInTelegram()) {
    return <NotInTelegramScreen />
  }

  if (maintenance) {
    return <MaintenanceScreen />
  }

  return (
    <OverlayProvider>
      <MeProvider>
      <SettingsDrawerProvider>
        <ProfileDrawerProvider>
        <LiveMatchSheetProvider>
          <SettingsDrawer />
          <ProfileDrawer />
          <LiveMatchSheet />
          <OrientationPreview />
          <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Non-game routes wrapped in AppShell (TopBar + BackButton LIFO). */}
          <Route element={<AppShell />}>
            <Route path="/" element={<Lobby />} />
            <Route path="/leaderboards" element={<Leaderboards />} />
            <Route path="/daily/results" element={<DailyResults />} />
            <Route path="/daily/history" element={<DailyHistory />} />
            <Route path="/match/history" element={<MatchHistory />} />
            {/* /match/results needs MatchProvider context (useMatchContext) AND
                AppShell chrome — nest MatchRoutesWrapper inside AppShell group.
                W3+W4 P1-e: /match/room (RoomLobby) also belongs INSIDE AppShell
                (this is NOT the game canvas; the canvas opens at /match after
                start). Lazy-loaded per Z4 bundle ratchet. */}
            <Route element={<MatchRoutesWrapper />}>
              <Route path="/match/results" element={<MatchResults />} />
              <Route path="/match/room" element={<RoomLobby />} />
            </Route>
            <Route path="/admin" element={<AdminGate />}>
              <Route index element={<Navigate to="/admin/overview" replace />} />
              <Route path="overview" element={<AdminOverview />} />
              <Route path="anti-cheat" element={<AdminAntiCheat />} />
              <Route path="matches" element={<AdminMatches />} />
            </Route>
          </Route>

          {/* Game routes — bypass AppShell entirely. */}
          <Route path="/freeplay" element={<FreePlay />} />
          <Route path="/freeplay/results" element={<Results />} />
          <Route path="/daily" element={<DailyChallenge />} />

          {/* Live-match active routes — MatchRoutesWrapper kept as sibling of
              AppShell (NOT child) so the WS lifecycle is unaffected. */}
          <Route element={<MatchRoutesWrapper />}>
            <Route path="/matchmaking" element={<Matchmaking />} />
            <Route path="/match" element={<MatchActive />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
        </LiveMatchSheetProvider>
        </ProfileDrawerProvider>
      </SettingsDrawerProvider>
      </MeProvider>
    </OverlayProvider>
  )
}
